import {
  useMemo,
  useState,
} from "react";

import {
  Button,
  Card,
  PageContainer,
  type ComponentDiagnostic,
} from "@swot/base-components";

import {
  FiDownload,
  FiPlus,
  FiRefreshCcw,
} from "react-icons/fi";

import {
  CmtsSmartDataGrid,
  type CmtsGridColumn,
  type CmtsGridFilterControl,
} from "@components/data-grid/index.js";

import {
  CmtsPageHintPanel,
} from "@components/hints/index.js";

import {
  CmtsEnterpriseModal,
} from "@components/CmtsEnterpriseModal.js";

import {
  throwCmtsCanonicalError,
} from "@cmts/api/cmtsApiErrors.js";

type ExportRow =
  Record<string, unknown>;

type ExportActionCardProps = {
  icon: string;
  title: string;
  description: string;
  disabled?: boolean;
  danger?: boolean;
  onClick: () => void;
};

function text(
  row: Record<string, unknown>,
  keys: readonly string[],
  fallback = "",
): string {
  for (const key of keys) {
    const value = row[key];

    if (
      value !== undefined &&
      value !== null &&
      String(value).trim()
    ) {
      return String(value);
    }
  }

  return fallback;
}

function exportId(
  row: Record<string, unknown>,
): string {
  return text(
    row,
    ["exportId", "id"],
  );
}

function exportStatus(
  row: Record<string, unknown>,
): string {
  return text(
    row,
    ["status"],
    "UNKNOWN",
  )
    .trim()
    .toUpperCase();
}

function exportFormat(
  row: Record<string, unknown>,
): string {
  return text(
    row,
    ["format", "exportFormat"],
    "JSON",
  )
    .trim()
    .toUpperCase();
}

function reportCode(
  row: Record<string, unknown>,
): string {
  return text(
    row,
    ["reportCode", "report", "code"],
    "cmts-report",
  );
}

function isCompleted(
  row: Record<string, unknown>,
): boolean {
  return [
    "COMPLETED",
    "READY",
  ].includes(exportStatus(row));
}

function isProcessing(
  row: Record<string, unknown>,
): boolean {
  return [
    "PENDING",
    "QUEUED",
    "PROCESSING",
    "IN_PROGRESS",
    "RETRY_REQUESTED",
  ].includes(exportStatus(row));
}

function isFailed(
  row: Record<string, unknown>,
): boolean {
  return [
    "FAILED",
    "BLOCKED",
  ].includes(exportStatus(row));
}

function isCancelled(
  row: Record<string, unknown>,
): boolean {
  return [
    "CANCELLED",
    "EXPIRED",
  ].includes(exportStatus(row));
}

function downloadReady(
  row: Record<string, unknown>,
): boolean {
  const explicitReady =
    row.downloadReady;

  if (
    typeof explicitReady === "boolean"
  ) {
    return (
      explicitReady &&
      isCompleted(row)
    );
  }

  return isCompleted(row);
}

function hasOperationReference(
  row: Record<string, unknown>,
): boolean {
  return Boolean(
    text(row, [
      "correlationId",
      "operationSpineEventId",
      "operationId",
    ]),
  );
}

function formatDateTime(
  row: Record<string, unknown>,
): string {
  const value = text(row, [
    "exportedAt",
    "completedAt",
    "requestedAt",
    "createdAt",
  ]);

  if (!value) {
    return "-";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString(
    undefined,
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    },
  );
}

function statusClass(
  row: Record<string, unknown>,
): string {
  return (
    "cmts-report-export-status--" +
    exportStatus(row).toLowerCase()
  );
}

function requireReportExportHandler(
  name: string,
): never {
  throwCmtsCanonicalError({
    code:
      "CMTS_ERR_SIDE_EFFECT_UNAVAILABLE",
    message:
      `CMTS Report Export action ${name} requires connected repository handlers.`,
    details: {
      action: name,
    },
  });
}

function ExportActionCard({
  icon,
  title,
  description,
  disabled = false,
  danger = false,
  onClick,
}: ExportActionCardProps) {
  return (
    <button
      type="button"
      className={[
        "cmts-notification-action-option",
        danger
          ? "cmts-notification-action-option--danger"
          : "",
      ]
        .filter(Boolean)
        .join(" ")}
      disabled={disabled}
      onClick={onClick}
    >
      <span
        className="cmts-notification-action-option__icon"
        aria-hidden="true"
      >
        {icon}
      </span>

      <span className="cmts-notification-action-option__content">
        <strong>{title}</strong>
        <small>{description}</small>
      </span>

      <span
        className="cmts-notification-action-option__arrow"
        aria-hidden="true"
      >
        ›
      </span>
    </button>
  );
}

export type CmtsReportExportsPageProps = {
  rows?:
    | readonly Record<
        string,
        unknown
      >[]
    | undefined;

  diagnostics?:
    | readonly ComponentDiagnostic[]
    | undefined;

  busy?: boolean | undefined;

  error?:
    | string
    | null
    | undefined;

  onRequestExport?:
    | ((
        payload?: Record<
          string,
          unknown
        >,
      ) => void | Promise<void>)
    | undefined;

  onDownload?:
    | ((
        id: string,
      ) => void | Promise<void>)
    | undefined;

  onRetryExport?:
    | ((
        row: Record<
          string,
          unknown
        >,
      ) => void | Promise<void>)
    | undefined;

  onCancelExport?:
    | ((
        row: Record<
          string,
          unknown
        >,
      ) => void | Promise<void>)
    | undefined;

  onOpenSourceReport?:
    | ((
        row: Record<
          string,
          unknown
        >,
      ) => void | Promise<void>)
    | undefined;

  onOpenAuditTrail?:
    | ((
        row: Record<
          string,
          unknown
        >,
      ) => void | Promise<void>)
    | undefined;

  onOpenOperationSpine?:
    | ((
        row: Record<
          string,
          unknown
        >,
      ) => void | Promise<void>)
    | undefined;
};

export function CmtsReportExportsPage(
  props: CmtsReportExportsPageProps,
) {
  const rows =
    (props.rows ??
      []) as readonly ExportRow[];

  const [
    actionsFor,
    setActionsFor,
  ] = useState<ExportRow | null>(
    null,
  );

  const [
    createOpen,
    setCreateOpen,
  ] = useState(false);

  const [
    createReportCode,
    setCreateReportCode,
  ] = useState(
    "complaint-register",
  );

  const [
    createFormat,
    setCreateFormat,
  ] = useState("JSON");

  const [
    createMaxRows,
    setCreateMaxRows,
  ] = useState(10_000);

  const [
    includeInternalNotes,
    setIncludeInternalNotes,
  ] = useState(false);

  const counts = useMemo(
    () => ({
      total: rows.length,

      processing:
        rows.filter(isProcessing)
          .length,

      completed:
        rows.filter(isCompleted)
          .length,

      failed:
        rows.filter(isFailed)
          .length,
    }),
    [rows],
  );

  const runAction = async (
    action:
      () => void | Promise<void>,
    closeAfterSuccess = true,
  ): Promise<void> => {
    try {
      await action();

      if (closeAfterSuccess) {
        setActionsFor(null);
      }
    } catch {
      /*
       * Container exposes mutation error
       * through props.error.
       * Modal remains open.
       */
    }
  };

  const createExport =
    async (): Promise<void> => {
      const normalizedReportCode =
        createReportCode.trim();

      const normalizedFormat =
        createFormat
          .trim()
          .toUpperCase();

      if (!normalizedReportCode) {
        return;
      }

      if (
        ![
          "CSV",
          "XLSX",
          "JSON",
        ].includes(
          normalizedFormat,
        )
      ) {
        return;
      }

      const action =
        props.onRequestExport;

      if (!action) {
        requireReportExportHandler(
          "create export",
        );
      }

      try {
        await action({
          reportCode:
            normalizedReportCode,

          format:
            normalizedFormat,

          filtersJson: "{}",

          maxRows:
            createMaxRows,

          includeInternalNotes,
        });

        setCreateOpen(false);
      } catch {
        /*
         * Keep create modal open.
         * Error is shown from props.error.
         */
      }
    };

  const columns: readonly CmtsGridColumn<
    Record<string, unknown>
  >[] = [
    {
      id: "reportCode",
      header: "Report",
      sortable: true,
      filterable: true,
      minWidth: 185,

      render: (row) => (
        <div className="cmts-report-export-name">
          <strong>
            {reportCode(row)}
          </strong>

          <span>
            {text(
              row,
              ["fileName"],
              "Report export",
            )}
          </span>
        </div>
      ),
    },

    {
      id: "status",
      header: "Status",
      sortable: true,
      filterable: true,
      minWidth: 125,

      render: (row) => (
        <span
          className={[
            "cmts-status-chip",
            statusClass(row),
          ].join(" ")}
        >
          {exportStatus(row)}
        </span>
      ),
    },

    {
      id: "format",
      header: "Format",
      sortable: true,
      filterable: true,
      minWidth: 95,

      render: (row) => (
        <span className="cmts-report-export-format">
          {exportFormat(row)}
        </span>
      ),
    },

    {
      id: "rowCount",
      header: "Rows",
      sortable: true,
      filterable: false,
      minWidth: 85,

      render: (row) =>
        text(
          row,
          ["rowCount"],
          "0",
        ),
    },

    {
      id: "fileName",
      header: "File",
      sortable: true,
      filterable: true,
      minWidth: 220,

      render: (row) => (
        <div className="cmts-report-export-file">
          <strong>
            {text(
              row,
              ["fileName"],
              "File pending",
            )}
          </strong>

          <span>
            {text(
              row,
              ["contentType"],
              exportFormat(row),
            )}
          </span>
        </div>
      ),
    },

    {
      id: "requestedAt",
      header: "Requested",
      sortable: true,
      filterable: true,
      minWidth: 165,

      render: (row) =>
        formatDateTime(row),
    },

    {
      id: "requestedByUserId",
      header: "Requested By",
      sortable: true,
      filterable: true,
      minWidth: 155,

      render: (row) =>
        text(
          row,
          [
            "requestedByName",
            "requestedBy",
            "requestedByUserId",
          ],
          "-",
        ),
    },

    {
      id: "exportActions",
      header: "Actions",
      sortable: false,
      filterable: false,
      minWidth: 125,

      render: (row) => (
        <button
          type="button"
          className="cmts-notification-actions-button"
          aria-label="Open report export actions"
          onClick={(event) => {
            event.stopPropagation();

            setActionsFor(
              row as ExportRow,
            );
          }}
        >
          <span
            className="cmts-notification-actions-button__icon"
            aria-hidden="true"
          >
            ⋮
          </span>

          <span>Actions</span>
        </button>
      ),
    },
  ];

  const filters: readonly CmtsGridFilterControl<
    Record<string, unknown>
  >[] = [
    {
      id: "status",
      label: "Status",
      columnId: "status",
      operator: "contains",
      placeholder: "All statuses",
    },

    {
      id: "reportCode",
      label: "Report",
      columnId: "reportCode",
      operator: "contains",
      placeholder: "All reports",
    },

    {
      id: "format",
      label: "Format",
      columnId: "format",
      operator: "contains",
      placeholder: "All formats",
    },
  ];

  const selectedCanDownload =
    actionsFor !== null &&
    downloadReady(actionsFor);

  const selectedCanRetry =
    actionsFor !== null &&
    isFailed(actionsFor);

  const selectedCanCancel =
    actionsFor !== null &&
    isProcessing(actionsFor);

  const selectedHasExportId =
    actionsFor !== null &&
    Boolean(
      exportId(actionsFor),
    );

  const selectedHasOperation =
    actionsFor !== null &&
    hasOperationReference(
      actionsFor,
    );

  return (
    <PageContainer
      title="Report Export History"
      subtitle="Generate, monitor and download CSV, Excel and JSON report exports."
    >
      <section className="cmts-report-export-header">
        <div className="cmts-report-export-header__content">
          <span className="cmts-report-export-header__eyebrow">
            REPORTING WORKSPACE
          </span>

          <h2>
            Report export lifecycle
          </h2>

          <p>
            Track export progress,
            download completed files and
            manage failed or pending
            report requests.
          </p>
        </div>

        <div className="cmts-report-export-header__actions">
          <Button
            intent="button"
            disabled={props.busy}
            onClick={() => {
              globalThis.location.reload();
            }}
          >
            <FiRefreshCcw />
            Refresh
          </Button>

          <Button
            className="cmts-action-button"
            intent="button"
            disabled={props.busy}
            onClick={() => {
              setCreateOpen(true);
            }}
          >
            <FiPlus />
            New Export
          </Button>
        </div>
      </section>

      <CmtsPageHintPanel>
        Completed exports are available
        for download. Failed or blocked
        exports remain visible for audit
        and can be retried.
      </CmtsPageHintPanel>

      <section className="cmts-report-export-kpis">
        <Card title="Total exports">
          <strong>
            {counts.total}
          </strong>

          <p>
            All export requests
          </p>
        </Card>

        <Card title="Processing">
          <strong>
            {counts.processing}
          </strong>

          <p>
            Queued or running
          </p>
        </Card>

        <Card title="Completed">
          <strong>
            {counts.completed}
          </strong>

          <p>
            Ready for download
          </p>
        </Card>

        <Card title="Failed">
          <strong>
            {counts.failed}
          </strong>

          <p>
            Requires attention
          </p>
        </Card>
      </section>

      <CmtsSmartDataGrid<
        Record<string, unknown>
      >
        title="Export history"
        subtitle="Search, filter and manage generated report exports."
        rows={rows}
        columns={columns}
        filters={filters}
        rowKey="exportId"
        defaultPageSize={10}
        searchable
        sortable
        filterable
        exportable={false}
        loading={props.busy}
        error={props.error}
        emptyMessage="No report exports have been generated for this tenant."
        filterSummary={[
          `${counts.total} total exports`,
          `${counts.completed} completed`,
          `${counts.failed} failed`,
        ]}
      />

      {/* New Export modal */}
      <CmtsEnterpriseModal
        open={createOpen}
        title="Create report export"
        subtitle="Choose the report, output format and maximum number of rows."
        size="md"
        onClose={() => {
          if (!props.busy) {
            setCreateOpen(false);
          }
        }}
        footer={
          <div className="cmts-report-export-modal-footer">
            <Button
              intent="button"
              disabled={props.busy}
              onClick={() => {
                setCreateOpen(false);
              }}
            >
              Cancel
            </Button>

            <Button
              className="cmts-action-button"
              intent="button"
              disabled={
                props.busy ||
                !createReportCode.trim()
              }
              onClick={() => {
                void createExport();
              }}
            >
              <FiDownload />
              Generate Export
            </Button>
          </div>
        }
      >
        <div className="cmts-report-export-create-form">
          {props.error && (
            <div
              className="cmts-notification-action-modal__error"
              role="alert"
            >
              {props.error}
            </div>
          )}

          <label>
            <span>Report</span>

            <select
              value={createReportCode}
              disabled={props.busy}
              onChange={(event) => {
                setCreateReportCode(
                  event.target.value,
                );
              }}
            >
              <option value="complaint-register">
                Complaint Register
              </option>

              <option value="sla-breach">
                SLA Breach
              </option>

              <option value="ageing">
                Complaint Ageing
              </option>
            </select>
          </label>

          <label>
            <span>Format</span>

            <select
              value={createFormat}
              disabled={props.busy}
              onChange={(event) => {
                setCreateFormat(
                  event.target.value,
                );
              }}
            >
              <option value="CSV">
                CSV
              </option>

              <option value="XLSX">
                Excel (.xlsx)
              </option>

              <option value="JSON">
                JSON
              </option>
            </select>
          </label>

          <label>
            <span>Maximum rows</span>

            <input
              type="number"
              min={1}
              max={100_000}
              step={100}
              value={createMaxRows}
              disabled={props.busy}
              onChange={(event) => {
                const value = Number(
                  event.target.value,
                );

                setCreateMaxRows(
                  Number.isFinite(value) &&
                  value > 0
                    ? Math.min(
                        Math.trunc(value),
                        100_000,
                      )
                    : 10_000,
                );
              }}
            />
          </label>

          <label className="cmts-report-export-checkbox">
            <input
              type="checkbox"
              checked={includeInternalNotes}
              disabled={props.busy}
              onChange={(event) => {
                setIncludeInternalNotes(
                  event.target.checked,
                );
              }}
            />

            <span>
              <strong>
                Include internal notes
              </strong>

              <small>
                Include restricted internal
                report notes when permitted.
              </small>
            </span>
          </label>
        </div>
      </CmtsEnterpriseModal>

      {/* Export Actions modal */}
      <CmtsEnterpriseModal
        open={Boolean(actionsFor)}
        title="Report export actions"
        subtitle={
          actionsFor
            ? `${reportCode(
                actionsFor,
              )} • ${exportFormat(
                actionsFor,
              )}`
            : ""
        }
        size="md"
        onClose={() => {
          if (!props.busy) {
            setActionsFor(null);
          }
        }}
      >
        {actionsFor && (
          <div className="cmts-notification-action-modal">
            <div className="cmts-notification-action-modal__summary">
              <div>
                <span>
                  Current status
                </span>

                <strong>
                  {exportStatus(
                    actionsFor,
                  )}
                </strong>
              </div>

              <div>
                <span>Rows</span>

                <strong>
                  {text(
                    actionsFor,
                    ["rowCount"],
                    "0",
                  )}
                </strong>
              </div>

              <div>
                <span>Requested</span>

                <strong>
                  {formatDateTime(
                    actionsFor,
                  )}
                </strong>
              </div>
            </div>

            {props.error && (
              <div
                className="cmts-notification-action-modal__error"
                role="alert"
              >
                {props.error}
              </div>
            )}

            <div className="cmts-notification-action-modal__grid">
              <ExportActionCard
                icon="↗"
                title="Open source report"
                description="Open the report with its current report code and filters."
                disabled={
                  props.busy ||
                  !props.onOpenSourceReport
                }
                onClick={() => {
                  const selected =
                    actionsFor;

                  void runAction(() =>
                    props.onOpenSourceReport
                      ? props.onOpenSourceReport(
                          selected,
                        )
                      : requireReportExportHandler(
                          "open source report",
                        ),
                  );
                }}
              />

              <ExportActionCard
                icon="↓"
                title="Download export"
                description="Download the completed CSV, Excel or JSON report file."
                disabled={
                  props.busy ||
                  !selectedCanDownload ||
                  !selectedHasExportId
                }
                onClick={() => {
                  const selected =
                    actionsFor;

                  void runAction(() =>
                    props.onDownload
                      ? props.onDownload(
                          exportId(
                            selected,
                          ),
                        )
                      : requireReportExportHandler(
                          "download export",
                        ),
                  );
                }}
              />

              <ExportActionCard
                icon="↻"
                title="Retry export"
                description="Retry a failed or blocked report export request."
                disabled={
                  props.busy ||
                  !selectedCanRetry
                }
                onClick={() => {
                  const selected =
                    actionsFor;

                  void runAction(() =>
                    props.onRetryExport
                      ? props.onRetryExport(
                          selected,
                        )
                      : requireReportExportHandler(
                          "retry export",
                        ),
                  );
                }}
              />

              <ExportActionCard
                icon="◉"
                title="Open audit trail"
                description="Review export creation, status and download audit events."
                disabled={
                  props.busy ||
                  !selectedHasExportId
                }
                onClick={() => {
                  const selected =
                    actionsFor;

                  void runAction(() =>
                    props.onOpenAuditTrail
                      ? props.onOpenAuditTrail(
                          selected,
                        )
                      : requireReportExportHandler(
                          "open audit trail",
                        ),
                  );
                }}
              />

              <ExportActionCard
                icon="⌁"
                title="Operation spine"
                description="Open the linked background operation and processing evidence."
                disabled={
                  props.busy ||
                  !selectedHasOperation
                }
                onClick={() => {
                  const selected =
                    actionsFor;

                  void runAction(() =>
                    props.onOpenOperationSpine
                      ? props.onOpenOperationSpine(
                          selected,
                        )
                      : requireReportExportHandler(
                          "open operation spine",
                        ),
                  );
                }}
              />

              <ExportActionCard
                icon="i"
                title="Export details"
                description={`File: ${text(
                  actionsFor,
                  ["fileName"],
                  "Pending",
                )}`}
                disabled
                onClick={() => undefined}
              />
            </div>

            <div className="cmts-notification-action-modal__danger">
              <ExportActionCard
                icon="×"
                title={
                  isCancelled(actionsFor)
                    ? "Export cancelled"
                    : "Cancel export"
                }
                description="Cancel this pending or processing report export request."
                danger
                disabled={
                  props.busy ||
                  !selectedCanCancel
                }
                onClick={() => {
                  const selected =
                    actionsFor;

                  void runAction(() =>
                    props.onCancelExport
                      ? props.onCancelExport(
                          selected,
                        )
                      : requireReportExportHandler(
                          "cancel export",
                        ),
                  );
                }}
              />
            </div>
          </div>
        )}
      </CmtsEnterpriseModal>
    </PageContainer>
  );
}